import { type ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import EvergreenMp4UploadDialog, {
  type EvergreenMp4UploadOutcome,
} from '@/components/pages/create-webinar/config/EvergreenMp4UploadDialog';
import ReplacementCutsDialog from '@/components/pages/create-webinar/config/ReplacementCutsDialog';
import type {
  EvergreenTimelineEvent,
  WebinarSessionConfigurationSession,
} from '@/features/webinar/types';
import {
  checkEvergreenMp4File,
  inspectEvergreenMp4Video,
  readVideoDurationSeconds,
  type EvergreenMp4UploadError,
} from '@/lib/evergreenMp4File';
import {
  checkReplacementAgainstTimeline,
  findNextUpcomingSessionDate,
  hasSessionInProgress,
  type Mp4ReplacementDeclaration,
  type ReplacementCheckFailure,
} from '@/lib/evergreenMp4Replacement';
import { webinarService } from '@/services/webinar.service';

type ReplacementCandidate = {
  file: File;
  durationSeconds: number;
  timeline: EvergreenTimelineEvent[];
};

type Props = {
  /** `null` : la vidéo est encore l'enregistrement du Live source. */
  videoName: string | null;
  /** Durée de la vidéo actuelle, référence des coupures déclarées. */
  currentDurationSeconds: number | null;
  pendingFileName: string | null;
  workspaceId: number | null;
  webinarId: number | null;
  sessions: WebinarSessionConfigurationSession[] | undefined;
  timezone: string;
  maxDurationSeconds: number | null;
  onReplaced: () => void;
  /** `importFromLive` : premier MP4 d'un Evergreen dont la vidéo est encore celle d'un Live. */
  variant?: 'replace' | 'importFromLive';
  /** Remplacement momentanément impossible, avec sa raison. */
  disabledReason?: string | null;
};

export default function ImportedEvergreenVideo({
  videoName,
  currentDurationSeconds,
  pendingFileName,
  workspaceId,
  webinarId,
  sessions,
  timezone,
  maxDurationSeconds,
  onReplaced,
  variant = 'replace',
  disabledReason = null,
}: Props) {
  const { t, i18n } = useTranslation('createWebinar');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [fileError, setFileError] = useState<EvergreenMp4UploadError | null>(null);
  const [checkFailure, setCheckFailure] = useState<{
    reason: ReplacementCheckFailure;
    file: File;
  } | null>(null);
  const [candidate, setCandidate] = useState<ReplacementCandidate | null>(null);
  const [uploading, setUploading] = useState<{
    file: File;
    replacement: Mp4ReplacementDeclaration;
  } | null>(null);

  const canReplace = workspaceId != null && webinarId != null;
  const importsFromLive = variant === 'importFromLive';
  const sessionInProgress = hasSessionInProgress(sessions);
  const nextSessionDate = findNextUpcomingSessionDate(sessions, new Date());
  const nextSessionLabel = nextSessionDate
    ? DateTime.fromISO(nextSessionDate, { zone: 'utc' })
        .setZone(timezone)
        .setLocale(i18n.language ?? 'fr')
        .toFormat("EEEE d MMMM yyyy - HH:mm'h'")
    : null;

  const selectFile = async (file: File) => {
    if (workspaceId == null || webinarId == null) return;
    setFileError(null);
    setCheckFailure(null);
    const basicError = checkEvergreenMp4File(file);
    if (basicError) {
      setFileError(basicError);
      return;
    }
    setIsChecking(true);
    try {
      const videoError = await inspectEvergreenMp4Video(file, maxDurationSeconds);
      if (videoError) {
        setFileError(videoError);
        return;
      }
      const [durationSeconds, timeline] = await Promise.all([
        readVideoDurationSeconds(file),
        webinarService.getEvergreenTimeline(workspaceId, webinarId).catch(() => null),
      ]);
      // Le contrôle « vidéo trop courte » se fait dans la confirmation, après les coupures.
      const check = checkReplacementAgainstTimeline(durationSeconds, timeline);
      if (check.kind === 'failed' || durationSeconds == null || timeline == null) {
        setCheckFailure({
          reason: check.kind === 'failed' ? check.reason : 'DURATION_UNREADABLE',
          file,
        });
      } else {
        setCandidate({ file, durationSeconds, timeline });
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void selectFile(file);
  };

  const confirmReplacement = (replacement: Mp4ReplacementDeclaration) => {
    if (!candidate) return;
    setUploading({ file: candidate.file, replacement });
    setCandidate(null);
  };

  const handleUploadFinished = (outcome: EvergreenMp4UploadOutcome) => {
    setUploading(null);
    if (outcome === 'ready') {
      toast.success(t('sessionForm.mp4.replace.success'));
      onReplaced();
      return;
    }
    if (outcome === 'pendingPromotion') {
      toast.info(t('sessionForm.mp4.replace.pendingPromotion'));
      onReplaced();
      return;
    }
    toast.warning(t('sessionForm.mp4.replace.notReplaced'));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 border border-input px-4 py-3 rounded-lg">
        <div className="size-10 shrink-0 bg-medium rounded-lg flex items-center justify-center">
          <img src="/assets/attach.svg" alt="" />
        </div>
        <div className="min-w-0 flex-1">
          {importsFromLive ? (
            <p className="text-sm text-tertiary">{t('sessionForm.mp4.importFromLive.hint')}</p>
          ) : (
            <>
              <p className="text-xs text-tertiary">
                {t(videoName ? 'sessionForm.mp4.importedVideo' : 'sessionForm.mp4.evergreenVideo')}
              </p>
              <p className="text-black text-base font-medium truncate">
                {videoName ?? t('sessionForm.mp4.liveRecording')}
              </p>
            </>
          )}
        </div>
        {canReplace && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".mp4,video/mp4"
              onChange={handleInputChange}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 text-back border-black"
              disabled={isChecking || sessionInProgress || disabledReason != null}
              onClick={() => inputRef.current?.click()}
            >
              {isChecking
                ? t('sessionForm.mp4.checking')
                : t(
                    importsFromLive
                      ? 'sessionForm.mp4.importFromLive.button'
                      : 'sessionForm.mp4.replace.button',
                  )}
            </Button>
          </>
        )}
      </div>
      {pendingFileName && (
        <p className="text-xs text-secondary">
          {t('sessionForm.mp4.replace.pendingNotice', { fileName: pendingFileName })}
        </p>
      )}
      {canReplace && sessionInProgress && (
        <p className="text-xs text-secondary">{t('sessionForm.mp4.replace.sessionInProgress')}</p>
      )}
      {canReplace && !sessionInProgress && disabledReason && (
        <p className="text-xs text-secondary">{disabledReason}</p>
      )}
      {fileError && (
        <p className="text-xs text-error">{t(`sessionForm.mp4.errors.${fileError}`)}</p>
      )}
      {checkFailure && (
        <div className="flex items-center gap-2">
          <p className="text-xs text-error">
            {t(`sessionForm.mp4.replace.checkErrors.${checkFailure.reason}`)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={isChecking}
            onClick={() => void selectFile(checkFailure.file)}
          >
            {t('sessionForm.mp4.dialog.retry')}
          </Button>
        </div>
      )}

      {candidate && (
        <ReplacementCutsDialog
          file={candidate.file}
          timeline={candidate.timeline}
          previousDurationSeconds={currentDurationSeconds}
          newDurationSeconds={candidate.durationSeconds}
          webinarId={webinarId}
          nextSessionLabel={nextSessionLabel}
          onCancel={() => setCandidate(null)}
          onConfirm={confirmReplacement}
        />
      )}

      {uploading && workspaceId != null && webinarId != null && (
        <EvergreenMp4UploadDialog
          open
          workspaceId={workspaceId}
          webinarId={webinarId}
          file={uploading.file}
          replacesVideo
          replacement={uploading.replacement}
          onFinished={handleUploadFinished}
        />
      )}
    </div>
  );
}
